import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';

import { notifyMessage } from '../../../shared/constants/notify-message';
import { Flexible } from '../../../shared/interfaces/create-surveys.interface';
import { SelectedQuota } from '../../../shared/interfaces/quota.interface';
import { QuotaModalService } from '../quota-modal.service';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
@Component({
  selector: 'ps-ui-range-quota-row',
  templateUrl: './range-quota-row.component.html',
  styleUrls: ['./range-quota-row.component.css'],
})
export class RangeQuotaRowComponent implements OnInit, OnChanges {
  @Input() data!: any;
  @Input() flexible!: Flexible;
  @Output() readonly quotasChanged = new EventEmitter();
  disableLastRow: boolean = false;
  quotas = this.data?.quotas || this._fb.array([]);
  private _surveyGoal!: number;
  form = this._fb.group({
    quotas: this.quotas,
  });

  constructor(
    private _fb: FormBuilder,
    private _toastrService: ToasterService,
    private _quotaModalService: QuotaModalService
  ) {}

  ngOnInit(): void {
    this._surveyGoal = this.data.complete;
    if (this.data?.quotas?.length) {
      this._createQuotaRow(this.data.quotas);
    } else {
      this.addQuotaRow();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data?.currentValue?.quotas?.length) {
      this._updateQuotaRow(changes.data.currentValue.quotas);
    }

    if (changes.flexible?.currentValue) {
      this.flexible = changes.flexible.currentValue;
    }
  }

  private _createQuotaRow(quotas: SelectedQuota[]) {
    const control: FormArray = <FormArray>this.form.get('quotas');

    quotas.forEach((quota: SelectedQuota) => {
      let rowForm = this._fb.group({
        buyer_quota_id: [quota.buyer_quota_id || ''],
        min: [
          this.valueExist(quota.min) ? Number(quota.min) : null,
          Validators.required,
        ],
        max: [
          this.valueExist(quota.max) ? Number(quota.max) : null,
          Validators.required,
        ],
        number: [Number(quota.number) || null, Validators.required],
        percentage: [Number(quota.percentage) || null, Validators.required],
        minimum: [Number(quota.minimum) || null, Validators.required],
        maximum: [Number(quota.maximum) || null, Validators.required],
        suppliers: [quota.suppliers || []],
      });
      control.push(rowForm);
    });

    this.quotasChanged.emit(this.form.value.quotas);
  }

  addQuotaRow() {
    if (this.data.isSelectedForAida) {
      this._toastrService.error(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }

    const control: FormArray = <FormArray>this.form.get('quotas');
    if (this.disableLastRow) {
      return;
    }
    if (
      control.controls[0] &&
      (!control.controls[0].value.min ||
        !control.controls[0].value.max ||
        !control.controls[0].value.number ||
        !control.controls[0].value.percentage)
    ) {
      this._toastrService.error(
        notifyMessage.errorMessage.QUOTA_MODAL.EMPTY_RANGE_ROW
      );
      return;
    }
    let minValue = null;
    let maxValue = null;
    if (
      control.controls[0]?.value?.max &&
      Number(control.controls[0].value.max) <
        Number(this.data.qualification.data[0].maxValue)
    ) {
      maxValue = Number(control.controls[0].value.max);
      if (maxValue < this._surveyGoal) {
        minValue = maxValue + 1;
      }
    }

    const rowForm = this._fb.group({
      min: [minValue, Validators.required],
      max: [null, Validators.required],
      number: [null, Validators.required],
      percentage: [null, Validators.required],
      minimum: [null, Validators.required],
      maximum: [null, Validators.required],
    });

    control.insert(0, rowForm);
    this.quotasChanged.emit(this.form.value.quotas);
    if (
      (maxValue &&
        maxValue >= Number(this.data.qualification.data[0].maxValue) &&
        this.data.remaining === 0) ||
      this.data.remaining === 0
    ) {
      this.disableLastRow = true;
    }
  }

  private _updateQuotaRow(updateQuotas: SelectedQuota[]) {
    this.data.quotas = updateQuotas;
    const control: FormArray = <FormArray>this.form.get('quotas');
    if (control.controls.length) {
      updateQuotas.forEach((_quota: SelectedQuota, index: number) => {
        control.controls[index].patchValue(_quota);
      });
    }
  }

  deleteQuotaRow(index: number) {
    if (this.data.isSelectedForAida) {
      this._toastrService.error(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }

    const control: FormArray = <FormArray>this.form.get('quotas');
    control.removeAt(index);
    this.disableLastRow = false;
    this.quotasChanged.emit(this.form.value.quotas);
  }

  numberChange(event: Event, index: number) {
    let requiredNumber = Number((<HTMLInputElement>event.target).value);
    const control: FormArray = <FormArray>this.form.get('quotas');

    if (
      this._quotaModalService.isValidAllocation(
        this.data.complete,
        control,
        index
      )
    ) {
      let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
        requiredNumber,
        this.flexible
      );
      control.controls[index].patchValue({
        percentage: Math.round(
          (requiredNumber * 100) / Number(this.data.complete)
        ),
        minimum: minimum,
        maximum: maximum,
      });
    }

    this.quotasChanged.emit(this.form.value.quotas);
  }

  percentageChange(event: Event, index: number) {
    let value = Number((<HTMLInputElement>event.target).value);
    const control: FormArray = <FormArray>this.form.get('quotas');

    if (
      this._quotaModalService.isValidAllocation(
        this.data.complete,
        control,
        index
      )
    ) {
      const requiredCompletes = this._quotaModalService.getNumber(
        this.data.complete,
        this.form.value.quotas,
        value,
        index
      );
      let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
        requiredCompletes,
        this.flexible
      );
      control.controls[index].patchValue({
        number: requiredCompletes,
        minimum: minimum,
        maximum: maximum,
      });
    }

    this.quotasChanged.emit(this.form.value.quotas);
  }

  validateRange(event: Event, index: number) {
    let value = Number((<HTMLInputElement>event.target).value);
    const control: FormArray = <FormArray>this.form.get('quotas');

    if (
      value < this.data.qualification.data[0].minValue ||
      value > this.data.qualification.data[0].maxValue ||
      this._inValidRange(control, index, value) ||
      this._isRangeOverlap(control, index, value)
    ) {
      (<HTMLInputElement>event.target).value = '';
      this._toastrService.error(
        notifyMessage.warningMessage.QUOTA_MODAL.INVALID_RANGE
      );
    }
  }

  private _inValidRange(control: FormArray, index: number, value: number) {
    if (
      !control.controls[index].value.min ||
      !control.controls[index].value.max
    ) {
      return false;
    }

    if (
      Number(control.controls[index].value.min) >
      Number(control.controls[index].value.max)
    ) {
      return true;
    }

    return false;
  }

  private _isRangeOverlap(control: FormArray, index: number, value: number) {
    let overlap = false;

    control.controls.forEach((element, currentIndex) => {
      if (
        index !== currentIndex &&
        value >= Number(element.value.min) &&
        value <= Number(element.value.max)
      ) {
        overlap = true;
      }
    });

    return overlap;
  }

  getbuttonText() {
    let buttonText = `${this.data.qualification.data[0].preFix || ''}`;

    if (this.valueExist(this.data.qualification.data[0].minValue)) {
      buttonText += `${this.data.qualification.data[0].minValue}`;
    }

    buttonText += ` to ${this.data.qualification.data[0].preFix || ''}`;

    if (this.valueExist(this.data.qualification.data[0].maxValue)) {
      buttonText += `${this.data.qualification.data[0].maxValue}`;
    }

    return buttonText;
  }

  valueExist(value: number | string | undefined) {
    if (!value && value !== 0) {
      return false;
    }
    return true;
  }
}
